// AuditSessionManager - Handles independent audit session management
export class AuditSessionManager {
  constructor() {
    this.activeSessions = new Map();
  }

  // Store session with session-specific keys for independent management
  storeSession(sessionId, auditData, backendSessionId) {
    const sessionStorageKey = `auditResponse_${sessionId}`;
    localStorage.setItem(sessionStorageKey, JSON.stringify(auditData));
    localStorage.setItem('currentAuditSessionId', sessionId);
    localStorage.setItem('currentAuditResponse', JSON.stringify(auditData)); // Keep for backward compatibility
    localStorage.setItem('auditSessionId', backendSessionId);
    
    // Track in memory
    this.activeSessions.set(sessionId, {
      auditData,
      backendSessionId,
      createdAt: new Date().toISOString()
    });
    
    console.log(`✅ Session ${sessionId} stored successfully`);
  }

  // Update current session storage
  updateCurrentSession(auditData) {
    const currentSessionId = localStorage.getItem('currentAuditSessionId');
    if (currentSessionId) {
      const sessionStorageKey = `auditResponse_${currentSessionId}`;
      localStorage.setItem(sessionStorageKey, JSON.stringify(auditData));
      
      // Update memory tracking
      if (this.activeSessions.has(currentSessionId)) {
        const session = this.activeSessions.get(currentSessionId);
        session.auditData = auditData;
        session.lastUpdated = new Date().toISOString();
      }
    }
    localStorage.setItem('currentAuditResponse', JSON.stringify(auditData));
  }

  // Get current session ID
  getCurrentSessionId() {
    return localStorage.getItem('currentAuditSessionId');
  }

  // Check for existing drafts with session awareness
  async checkForDrafts(userId) {
    console.log('🔍 Checking for session-specific drafts...');
    
    // Look for session-specific drafts first
    const currentSessionId = localStorage.getItem('currentAuditSessionId');
    if (currentSessionId) {
      const sessionStorageKey = `auditResponse_${currentSessionId}`;
      const sessionAuditResponse = localStorage.getItem(sessionStorageKey);
      
      if (sessionAuditResponse) {
        console.log('📋 Found session-specific audit response');
        const auditResponse = JSON.parse(sessionAuditResponse);
        
        return {
          hasDraft: true,
          auditResponse,
          sessionId: currentSessionId,
          isIndependent: true
        };
      }
    }
    
    // Fallback to traditional draft checking
    const storedAuditResponse = localStorage.getItem('currentAuditResponse');
    const storedSessionId = localStorage.getItem('auditSessionId');
    
    if (storedAuditResponse && storedSessionId) {
      const auditResponse = JSON.parse(storedAuditResponse);
      
      return {
        hasDraft: true,
        auditResponse,
        sessionId: storedSessionId,
        isIndependent: false // Traditional draft
      };
    }
    
    return {
      hasDraft: false
    };
  }

  // Clean up completed session
  cleanupCompletedSession(sessionId) {
    if (sessionId) {
      const sessionStorageKey = `auditResponse_${sessionId}`;
      localStorage.removeItem(sessionStorageKey);
      localStorage.removeItem('currentAuditSessionId');
      this.activeSessions.delete(sessionId);
    }
    localStorage.removeItem('currentAuditResponse');
    localStorage.removeItem('auditSessionId');
    
    console.log(`🧹 Completed session ${sessionId} cleaned up`);
  }

  // Clean up invalid draft
  cleanupInvalidDraft() {
    const currentSessionId = localStorage.getItem('currentAuditSessionId');
    if (currentSessionId) {
      const sessionStorageKey = `auditResponse_${currentSessionId}`;
      localStorage.removeItem(sessionStorageKey);
      localStorage.removeItem('currentAuditSessionId');
      this.activeSessions.delete(currentSessionId);
    }
    localStorage.removeItem('currentAuditResponse');
    localStorage.removeItem('auditSessionId');
    
    console.log('🧹 Invalid draft cleaned up');
  }

  // Clear all draft data
  clearAllDraftData() {
    console.log('🧹 Clearing all session-specific draft data...');
    
    // Clear session-specific storage
    const currentSessionId = localStorage.getItem('currentAuditSessionId');
    if (currentSessionId) {
      const sessionStorageKey = `auditResponse_${currentSessionId}`;
      localStorage.removeItem(sessionStorageKey);
      localStorage.removeItem('currentAuditSessionId');
    }
    
    // Clear traditional storage
    localStorage.removeItem('currentAuditResponse');
    localStorage.removeItem('auditSessionId');
    
    // Clear all tracked sessions
    this.activeSessions.clear();
    
    console.log('✅ All draft data cleared');
  }

  // Get all stored audit sessions
  getAllStoredSessions() {
    const sessions = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('auditResponse_')) {
        try {
          const sessionData = JSON.parse(localStorage.getItem(key));
          const sessionId = key.replace('auditResponse_', '');
          sessions.push({
            sessionId,
            auditResponse: sessionData,
            storageKey: key
          });
        } catch (error) {
          console.warn(`Failed to parse session data for ${key}:`, error);
        }
      }
    }
    
    return sessions;
  }

  // Switch to a different audit session
  async switchToSession(sessionId, auditService) {
    console.log(`🔄 Switching to independent audit session: ${sessionId}`);
    
    const sessionStorageKey = `auditResponse_${sessionId}`;
    const sessionData = localStorage.getItem(sessionStorageKey);
    
    if (!sessionData) {
      throw new Error(`Session ${sessionId} not found in storage`);
    }
    
    try {
      const auditResponse = JSON.parse(sessionData);
      
      // Verify session still exists on server
      const result = await auditService.getAuditResponse(auditResponse.audit_form_response_id);
      
      if (!result.success) {
        throw new Error(`Session ${sessionId} no longer exists on server`);
      }
      
      // Update current session
      auditService.currentAuditResponse = result.auditResponse;
      auditService.isDraftMode = result.auditResponse.status === 'DRAFT';
      
      // Update localStorage pointers
      localStorage.setItem('currentAuditSessionId', sessionId);
      localStorage.setItem('currentAuditResponse', JSON.stringify(result.auditResponse));
      
      console.log(`✅ Switched to session ${sessionId} successfully`);
      
      return {
        success: true,
        auditResponse: result.auditResponse,
        sessionId
      };
      
    } catch (error) {
      console.error(`❌ Failed to switch to session ${sessionId}:`, error);
      throw error;
    }
  }

  // Get session summary
  getSessionSummary(sessionId) {
    const sessionStorageKey = `auditResponse_${sessionId}`;
    const sessionData = localStorage.getItem(sessionStorageKey);
    
    if (!sessionData) {
      return null;
    }
    
    try {
      const auditResponse = JSON.parse(sessionData);
      
      return {
        sessionId,
        auditFormResponseId: auditResponse.audit_form_response_id,
        formName: auditResponse.audit_form?.form_name || 'Unknown Form',
        status: auditResponse.status,
        createdAt: auditResponse.created_at,
        lastUpdated: auditResponse.updated_at,
        isActive: sessionId === localStorage.getItem('currentAuditSessionId'),
        hasData: !!auditResponse.raw_response_json?.cumulativeAuditData
      };
    } catch (error) {
      console.error(`Error parsing session ${sessionId}:`, error);
      return null;
    }
  }

  // Export audit data for backup/restore
  exportAuditData(sessionId = null) {
    const targetSessionId = sessionId || localStorage.getItem('currentAuditSessionId');
    if (!targetSessionId) {
      throw new Error('No session ID provided or current session found');
    }

    const sessionStorageKey = `auditResponse_${targetSessionId}`;
    const sessionData = localStorage.getItem(sessionStorageKey);
    
    if (!sessionData) {
      throw new Error(`Session ${targetSessionId} not found`);
    }

    const auditResponse = JSON.parse(sessionData);
    
    return {
      exportedAt: new Date().toISOString(),
      sessionId: targetSessionId,
      auditData: auditResponse,
      metadata: {
        version: '2.0',
        type: 'independent_audit_export',
        deviceInfo: this.getDeviceInfo()
      }
    };
  }

  // Import audit data from backup
  importAuditData(exportedData, auditService) {
    if (!exportedData || !exportedData.auditData) {
      throw new Error('Invalid export data format');
    }

    const { sessionId, auditData } = exportedData;
    
    // Validate the imported data
    if (!auditData.audit_form_response_id) {
      throw new Error('Missing audit form response ID in imported data');
    }

    // Store the imported data
    const sessionStorageKey = `auditResponse_${sessionId}`;
    localStorage.setItem(sessionStorageKey, JSON.stringify(auditData));
    
    // Track in memory
    this.activeSessions.set(sessionId, {
      auditData,
      importedAt: new Date().toISOString()
    });
    
    // Make it the current session if no current session exists
    if (!localStorage.getItem('currentAuditSessionId')) {
      localStorage.setItem('currentAuditSessionId', sessionId);
      localStorage.setItem('currentAuditResponse', JSON.stringify(auditData));
      auditService.currentAuditResponse = auditData;
      auditService.isDraftMode = auditData.status === 'DRAFT';
    }

    console.log(`✅ Imported audit data for session ${sessionId}`);
    
    return {
      success: true,
      sessionId,
      auditResponse: auditData
    };
  }

  // Clean up old sessions
  cleanupOldSessions(maxAge = 7 * 24 * 60 * 60 * 1000) { // 7 days default
    const sessions = this.getAllStoredSessions();
    const now = Date.now();
    let cleanedCount = 0;
    
    sessions.forEach(({ sessionId, auditResponse, storageKey }) => {
      const createdAt = new Date(auditResponse.created_at).getTime();
      const age = now - createdAt;
      
      if (age > maxAge && auditResponse.status === 'COMPLETED') {
        console.log(`🧹 Cleaning up old completed session: ${sessionId}`);
        localStorage.removeItem(storageKey);
        this.activeSessions.delete(sessionId);
        cleanedCount++;
      }
    });
    
    console.log(`✅ Cleaned up ${cleanedCount} old sessions`);
    return cleanedCount;
  }

  // Get device info for audit creation
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    
    let deviceType = 'WEB_CLIENT';
    if (/Mobi|Android/i.test(userAgent)) {
      deviceType = 'MOBILE_WEB';
    } else if (/Tablet|iPad/i.test(userAgent)) {
      deviceType = 'TABLET_WEB';
    }
    
    return {
      device_id: `${deviceType}_${Date.now()}`,
      user_agent: userAgent,
      platform: platform,
      isIndependent: true
    };
  }

  // Create multiple independent sessions
  createMultipleSessions(sessionConfigs) {
    const results = [];
    
    sessionConfigs.forEach(config => {
      try {
        const sessionId = `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Create session data structure
        const sessionData = {
          session_id: sessionId,
          audit_form_id: config.auditFormId,
          operator_id: config.operatorId,
          device_id: config.deviceId || this.getDeviceInfo().device_id,
          shift: config.shift || '',
          work_order: config.workOrder || '',
          status: 'DRAFT',
          created_at: new Date().toISOString(),
          raw_response_json: {
            sessionId,
            createdAt: new Date().toISOString(),
            isIndependent: true,
            cumulativeAuditData: {}
          }
        };
        
        // Store session
        this.storeSession(sessionId, sessionData, sessionId);
        
        results.push({
          success: true,
          sessionId,
          config
        });
        
      } catch (error) {
        results.push({
          success: false,
          error: error.message,
          config
        });
      }
    });
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Created ${successCount}/${sessionConfigs.length} independent sessions`);
    
    return {
      success: successCount > 0,
      results,
      successCount,
      totalCount: sessionConfigs.length
    };
  }

  // Get session statistics
  getSessionStatistics() {
    const sessions = this.getAllStoredSessions();
    const currentSessionId = this.getCurrentSessionId();
    
    const stats = {
      totalSessions: sessions.length,
      activeSessions: sessions.filter(s => s.auditResponse.status === 'DRAFT').length,
      completedSessions: sessions.filter(s => s.auditResponse.status === 'COMPLETED').length,
      currentSessionId,
      memoryTrackedSessions: this.activeSessions.size,
      storageUsage: this.calculateStorageUsage()
    };
    
    return stats;
  }

  // Calculate storage usage for sessions
  calculateStorageUsage() {
    let totalBytes = 0;
    const sessions = this.getAllStoredSessions();
    
    sessions.forEach(({ storageKey }) => {
      const data = localStorage.getItem(storageKey);
      if (data) {
        totalBytes += data.length;
      }
    });
    
    return {
      totalBytes,
      totalKB: Math.round(totalBytes / 1024),
      totalMB: Math.round(totalBytes / (1024 * 1024))
    };
  }

  // Bulk operations on sessions
  bulkSessionOperation(operation, sessionIds = null) {
    const targetSessions = sessionIds || this.getAllStoredSessions().map(s => s.sessionId);
    const results = [];
    
    targetSessions.forEach(sessionId => {
      try {
        let result;
        
        switch (operation) {
          case 'export':
            result = this.exportAuditData(sessionId);
            results.push({ sessionId, success: true, data: result });
            break;
            
          case 'cleanup':
            const sessionStorageKey = `auditResponse_${sessionId}`;
            localStorage.removeItem(sessionStorageKey);
            this.activeSessions.delete(sessionId);
            results.push({ sessionId, success: true, action: 'cleaned' });
            break;
            
          case 'validate':
            const summary = this.getSessionSummary(sessionId);
            const isValid = summary && summary.auditFormResponseId;
            results.push({ sessionId, success: true, isValid, summary });
            break;
            
          default:
            results.push({ sessionId, success: false, error: `Unknown operation: ${operation}` });
        }
        
      } catch (error) {
        results.push({ sessionId, success: false, error: error.message });
      }
    });
    
    const successCount = results.filter(r => r.success).length;
    console.log(`✅ Bulk ${operation}: ${successCount}/${targetSessions.length} successful`);
    
    return {
      operation,
      success: successCount > 0,
      results,
      successCount,
      totalCount: targetSessions.length
    };
  }

  // Reset session manager
  reset() {
    console.log('🔄 Resetting session manager...');
    this.activeSessions.clear();
    console.log('✅ Session manager reset complete');
  }
}